import { useEffect } from "react";

import { AttributeType } from "@energy-advice/easas-api";
import {
  ProductTemplateEntryType,
  useCreateProductTemplateEntries,
} from "api/api-hooks/product-templates-hooks";
import {
  IProductTemplate,
  ITableDataEntryModified,
  ITableDataEntryValue,
  MultiValueProps,
} from "components/dashboard/product-template-table/utils/interfaces";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import useCsvReader, { ParseResultData } from "hooks/useCsvReader";
import { T } from "services/lang/LanguageService";
import { CellSubType } from "utils/enums";
import { TimeSpan, convertStringToTimeSpan, toInstant } from "utils/time-utils";

const useProductTemplateImportEntries = (
  configData: IProductTemplate | undefined,
  params: ProductTemplateEntryType
) => {
  const { handleFileChange, data: csvData } = useCsvReader();

  const { mutate } = useCreateProductTemplateEntries(params, {
    onSuccess: () => {
      easasAlert({ summary: T("alert.success"), detail: T("alert.success"), severity: "success" });
    },
    onFailure: error => {
      easasAlert({ summary: T("alert.error"), detail: error.message, severity: "error" });
    },
  });

  useEffect(() => {
    if (csvData.length > 0 && configData?.data?.attributes && configData?.data?.attributes.length > 0) {
      const entriesModified: ITableDataEntryModified[] = csvData.map(row => {
        const entries = configData.data!.attributes.reduce((array: ITableDataEntryValue[], attr) => {
          if (
            attr.type === AttributeType.USER_SIGNATURE ||
            attr.type === AttributeType.FOREIGN_KEY ||
            attr.type === AttributeType.MULTI_FOREIGN_KEY ||
            attr.locked
          ) {
            return array;
          }
          let value = row[attr.displayName?.trim().toLocaleLowerCase() as keyof ParseResultData] as
            | number
            | string
            | TimeSpan
            | MultiValueProps;

          if (!value) {
            const keyWithUnit = `${attr.displayName?.trim().toLocaleLowerCase()} (${attr.unit})`;
            value = row[keyWithUnit as keyof ParseResultData] as number | string | TimeSpan | MultiValueProps;
          }

          if (value) {
            switch (attr.type) {
              case AttributeType.DOUBLE:
              case AttributeType.LONG:
                value = +value;
                break;
              case AttributeType.TIMESPAN:
                value = convertStringToTimeSpan(value as string);
                break;
              case AttributeType.INSTANT:
                value = toInstant(value as string)?.toString();
                break;
              case AttributeType.MULTI_VALUE: {
                let values: (string | number | TimeSpan)[] = [];
                switch (attr.subType) {
                  case CellSubType.TIMESPAN:
                    values = (value as string).split(" | ").map(v => convertStringToTimeSpan(v));
                    break;
                  case CellSubType.DOUBLE:
                  case CellSubType.LONG:
                    values = (value as string).split(" | ").map(v => +v);
                    break;
                  default:
                    values = (value as string).split(" | ");
                    break;
                }
                value = {
                  subType: attr.subType!,
                  values,
                };
                break;
              }
              default:
                break;
            }
            array.push({
              referenceName: attr.referenceName,
              type: attr.type,
              value,
            });
          }
          return array;
        }, []);
        return { templateRef: configData.safeName, entryData: entries };
      });

      if (configData?.safeName && configData.data && entriesModified.length > 0) {
        mutate(entriesModified);
      }
    }
  }, [configData?.data, configData?.safeName, csvData, mutate]);

  return { handleFileChange } as const;
};

export default useProductTemplateImportEntries;
